/**
 * Metrics API Service
 * 
 * Provides access to Grafana/Prometheus metrics via the Cloud API
 */

import { api } from './api';

export interface SafetySummary {
  ppe_violations: number;
  zone_intrusions: number;
  active_alerts: number;
  cameras_online: number;
  avg_latency_ms: number;
  /** Enabled cameras on online/running edge devices (DB-scoped). */
  cameras_on_online_devices?: number;
  /** Enabled cameras on offline edge devices (DB-scoped). */
  cameras_on_offline_devices?: number;
  devices_online?: number;
  /** Grafana/Prometheus firing alert count (not tenant-filtered). */
  prometheus_firing_alerts?: number;
  /** All unresolved incidents for the scoped org/site (can be large). */
  scoped_open_incidents_total?: number;
}

export interface Alert {
  uid?: string;
  alert_id?: string;
  alert_type?: string;
  title?: string;
  description?: string;
  state?: string;
  severity?: string;
  zone?: string;
  device_id?: string;
  timestamp?: string;
  resolved?: boolean;
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
}

export interface MetricDataPoint {
  timestamp: number;
  value: number;
}

export interface MetricResult {
  metric: Record<string, string>;
  values: [number, string][];
}

export interface PrometheusResponse {
  status: string;
  data: {
    resultType: string;
    result: MetricResult[];
  };
}

export interface ReliabilityMetricsResponse {
  ok: boolean;
  metrics?: Record<string, any>;
}

// API Functions
export const metricsApi = {
  /**
   * Get safety summary (current metrics snapshot)
   */
  getSafetySummary: async (): Promise<SafetySummary> => {
    const response = await api.get('/api/v1/metrics/safety/summary');
    return response.data;
  },

  /**
   * Get all alerts
   */
  getAlerts: async (state?: string): Promise<{ alerts: Alert[]; count: number }> => {
    const params = state ? { state } : {};
    const response = await api.get('/api/v1/metrics/alerts', { params });
    return response.data;
  },

  /**
   * Get active/firing alerts
   */
  getActiveAlerts: async (): Promise<{
    alerts: Alert[];
    /** Full open-incident count for tenant scope (not capped by page size). */
    count: number;
    total?: number;
    limit?: number;
    returned?: number;
  }> => {
    const response = await api.get('/api/v1/metrics/alerts/active');
    return response.data;
  },

  /**
   * Silence an alert
   */
  silenceAlert: async (alertUid: string, durationMinutes: number = 60, comment?: string) => {
    const response = await api.post(`/api/v1/metrics/alerts/${alertUid}/silence`, {
      duration_minutes: durationMinutes,
      comment: comment || 'Silenced via dashboard',
    });
    return response.data;
  },

  /**
   * Get alert history
   */
  getAlertHistory: async (limit: number = 100, from?: string) => {
    const params: Record<string, any> = { limit };
    if (from) params.from = from;
    const response = await api.get('/api/v1/metrics/alerts/history', { params });
    return response.data;
  },

  /**
   * Get analytics data from database (incidents over time)
   */
  getAnalyticsData: async (hours: number = 24): Promise<{
    incident_flow_over_time: Array<{ time: string; created: number; closed: number }>;
    ppe_over_time: any[];
    zone_over_time: any[];
    alerts_by_category: { name: string; value: number }[];
    severity_breakdown: { name: string; value: number }[];
    totals: { ppe_violations: number; zone_breaches: number; total_alerts: number };
  }> => {
    const response = await api.get('/api/v1/metrics/analytics/incidents-over-time', {
      params: { hours }
    });
    return response.data;
  },

  /**
   * Query Prometheus metrics
   */
  queryMetrics: async (
    expr: string,
    start?: string,
    end?: string,
    step: string = '60s'
  ): Promise<PrometheusResponse> => {
    const params: Record<string, any> = { expr, step };
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await api.get('/api/v1/metrics/query', { params });
    return response.data;
  },

  /**
   * Instant query
   */
  queryInstant: async (expr: string): Promise<PrometheusResponse> => {
    const response = await api.get('/api/v1/metrics/query/instant', { params: { expr } });
    return response.data;
  },

  /**
   * Get PPE violation metrics
   */
  getPPEMetrics: async (start?: string, end?: string) => {
    const params: Record<string, any> = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await api.get('/api/v1/metrics/safety/ppe', { params });
    return response.data;
  },

  /**
   * Get zone metrics
   */
  getZoneMetrics: async (zone?: string, start?: string, end?: string) => {
    const params: Record<string, any> = {};
    if (zone) params.zone = zone;
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await api.get('/api/v1/metrics/safety/zones', { params });
    return response.data;
  },

  /**
   * Reliability metrics rollup (events/evidence/latency/network context).
   */
  getReliabilityMetrics: async (params?: { window_hours?: number; device_id?: string }) => {
    const query: Record<string, any> = {};
    if (typeof params?.window_hours === 'number') query.window_hours = params.window_hours;
    if (params?.device_id) query.device_id = params.device_id;
    const response = await api.get('/api/v1/metrics/reliability', { params: query });
    return response.data as ReliabilityMetricsResponse;
  },

  /**
   * Check metrics service health
   */
  checkHealth: async (): Promise<{ healthy: boolean; service: string; url: string }> => {
    const response = await api.get('/api/v1/metrics/health');
    return response.data;
  },

  /**
   * List dashboards
   */
  listDashboards: async () => {
    const response = await api.get('/api/v1/metrics/dashboards');
    return response.data;
  },
};

export default metricsApi;
